import { Component, AfterViewInit, Inject, LOCALE_ID } from '@angular/core';
import { formatDate } from '@angular/common';
import { ModalController, NavParams, ToastController, AlertController, LoadingController } from '@ionic/angular';
import { Router } from '@angular/router';

import { GoalProgresses, GoalProgress, GoalComment } from '../../interfaces/goal-progresses.interface';
import { DashboardService } from '../../dashboard.service';
import { BaseClass } from 'src/app/shared/classes/base';
import { TranslationService } from 'src/app/shared/services/translation.service';

import * as Chart from 'chart.js';
import * as moment from 'moment/moment';
import { UtilService } from 'src/app/shared/services/util.service';

@Component({
  selector: 'app-view-goals',
  templateUrl: './view-goals.page.html',
  styleUrls: [ './view-goals.page.scss' ]
})
export class ViewGoalsPage extends BaseClass implements AfterViewInit {

  public goalProgress: GoalProgresses;
  public chart: any;
  public currentProgress: number;
  public newComment: string = '';
  public allowComments: boolean = true;

  private _subordinatedId: string;
  private _cycleId: number;
  private _goalId: number;
  private _shouldReload: boolean = false;

  constructor(
    protected _toastController: ToastController,
    protected _translationService: TranslationService,
    public modalController: ModalController,
    public alertController: AlertController,
    private _loadingController: LoadingController,
    private _dashboardService: DashboardService,
    private _navParams: NavParams,
    private _router: Router,
    private _utilService: UtilService,
    @Inject(LOCALE_ID) private locale: string
  ) {
    super(_toastController, _translationService);
    this._cycleId = this._navParams.get('cycleId');
    this._subordinatedId = this._navParams.get('subordinatedId');
    this._goalId = this._navParams.get('goalId');
    this.allowComments = this._navParams.get('allowComments') && true;
  }

  ngAfterViewInit() {
    this._loadGoalProgress();
  }

  public dismiss() {
    this.modalController.dismiss( this._shouldReload );
  }

  public goToGoals(): void {
    this.dismiss();
    this._router.navigate([ 'goals-input/' + this._cycleId ]);
  }

  public addNewProgress(goalId: number, progress: number): void {
    this._dashboardService.addGoalProgress(
      goalId, progress
    ).subscribe(() => {
      this._loadGoalProgress();
      this._shouldReload = true;
      this.notify('Progresso adicionado com sucesso', 'success');

    }, (error) => this.notify( this.getErrorNotification(error), 'danger' ));
  }

  public addNewComment(goalId: number, comment: string): void {
    this._dashboardService.addGoalComment(
      goalId, comment
    ).subscribe(() => {
      this._loadGoalProgress();
      this.notify('Comentário adicionado com sucesso', 'success');

    }, (error) => this.notify( this.getErrorNotification(error), 'danger' ));
  }

  public async confirmDeleteComment(goalId: number, commentId: number): Promise<void> {
    const alert = await this.alertController.create({
      message: this._translationService.getTranslation('goalProgressInfo.confirmDeleteComment'),
      buttons: [{
        text: this._translationService.getTranslation('generic.no'),
        role: 'cancel',
        cssClass: 'danger'
      }, {
        text: this._translationService.getTranslation('generic.yes'),
        handler: () => {
          this._deleteComment(goalId, commentId);
        }
      }]
    });

    await alert.present();
  }

  private _deleteComment(goalId: number, commentId: number): void {
    this._dashboardService.deleteGoalComment(
      goalId, commentId
    ).subscribe(() => {
      this._loadGoalProgress();
      this.notify('Comentário excluído com sucesso', 'success');

    }, (error) => this.notify( this.getErrorNotification(error), 'danger' ));
  }

  private _getDateFromValue(value: number): string {
    const progress = this.goalProgress.progress.find(p => p.progress === value);
    return progress ?
      formatDate(progress.date, 'dd/MM/yyyy', this.locale) + ' - ' : '';
  }

  public getDateDiff(goal: { createdAt: Date }): number {
    const momentDueDate = moment(goal.createdAt).startOf('day');
    const momentToday = moment().startOf('day');
    return moment.duration(momentToday.diff(momentDueDate)).asDays();
  }

  private async _loadGoalProgress(): Promise<void> {
    const loading = await this._loadingController.create();
    await loading.present();

    this._dashboardService.getGoalById(
      this._goalId,
      this._subordinatedId
    ).subscribe((response) => {
      this.goalProgress = response.data;
      this._createProgressChar( this.goalProgress );
      loading.dismiss();

    }, (error) => {
      loading.dismiss();
      this.notify( this.getErrorNotification(error), 'danger' );
    });
  }

  private _createProgressChar(goal: GoalProgresses): void {
    const DeadlineDate = new Date(goal.deadline);

    const afterDeadlineCount = goal.progress.reduce((sum, progress) => {
      progress.date = new Date(progress.date);
      return progress.date > DeadlineDate ? sum + 1 : sum;
    }, 0);

    const canvas = document.getElementById('canvasContainer') as any;
    this._registerCharPlugin( afterDeadlineCount, canvas?.offsetWidth );

    const hasProgressInDeadline = goal.progress.some(progress => {
      const ProgressDate = new Date(progress.date);
      ProgressDate.setHours(0, 0, 0, 0);
      DeadlineDate.setHours(0, 0, 0 , 0);
      return ProgressDate === DeadlineDate;
    });

    let latestAfterDeadline: GoalProgress | null = null;

    if (afterDeadlineCount > 0 && !hasProgressInDeadline) {
      const beforeDeadline = goal.progress.filter(progress => {
        const ProgressDate = new Date(progress.date);
        return ProgressDate < DeadlineDate;
      });

      const maxProgress = Math.max.apply(
        Math, beforeDeadline.map((p) => p.progress)
      );

      goal.progress.push({
        date: DeadlineDate,
        progress: maxProgress
      });

      latestAfterDeadline = goal.progress.reduce((progress, current) => {
        const ProgressDate = new Date(progress.date);
        return ProgressDate > DeadlineDate && progress.date > current.date ?
          progress : current;
      });
    }

    const data: Array<GoalProgress> = this._utilService.orderByProperty(
      goal.progress, 'date'
    );

    const adjustesData = data.map(p => {
      return {
        x: moment(p.date).format('DD/MM/YYYY'),
        y: p.progress
      };
    });

    adjustesData.splice(0, 0, {
      x: moment(goal.createdAt).format('DD/MM/YYYY'),
      y: 0
    });

    const ctx2 = document.getElementById('myChart') as HTMLCanvasElement;
    this.chart = new Chart(ctx2, <any>{
      type: 'line',
      data: {
        datasets: [{
            label: '',
            data: [ ...adjustesData ],
            backgroundColor: 'transparent',
            borderColor: '#FFFFFF',
            pointBackgroundColor: '#FFFFFF',
            pointBorderColor: '#FFFFFF',
            pointHoverBackgroundColor: '#001e62',
            pointHoverBorderColor: '#001e62',
            borderWidth: 6,
            pointRadius: 8,
            pointHoverRadius: 10
        }]
      },
      options: {
        legend: { display: false },
        responsive: true,
        maintainAspectRatio: false,
        animation : false,
        bezierCurve : true,
        tooltips: {
          enabled: true,
          callbacks: {
            label: ((tooltipItem, graphData) => {
              const value = graphData.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
              return value.x + ' - ' + value.y + '%';
            }).bind(this)
          }
        },
        chartArea: {
          filledBackgroundColor: '#195ab4',
          missingBackgroundColor: '#d2224b'
        },
        scales: {
          yAxes: [{
            gridLines: { display: false },
            ticks: {
              fontColor: '#001e62',
              fontSize: 11,
              fontStyle: 'bold',
              stepSize: 25,
              beginAtZero: true,
              min: 0,
              max: 100,
            }
          }, {
            display: afterDeadlineCount === 0,
            gridLines: { display: false },
            position: 'right',
            scaleLabel: {
              display: true,
              position: 'right',
              labelString: 'deadline',
              backgroundColor: '#d2224b',
              backdropColor: '#d2224b',
              fontColor: 'white',
              fontStyle: 'bold'
            },
            ticks: { display: false }
          }],
          xAxes: [{
            type: 'time',
            time: {
              displayFormats: {
                'month': 'MM/YYYY'
              },
              format: 'DD/MM/YYYY',
              tooltipFormat: 'll',
              min: moment(goal.createdAt).format('DD/MM/YYYY'),
              max: moment(
                latestAfterDeadline ? latestAfterDeadline.date : goal.deadline
              ).format('DD/MM/YYYY')
            },
            ticks: {
              fontColor: '#001e62',
              fontSize: 11,
              fontStyle: 'bold',
              display: true
            },
            gridLines: { display: false }
          }]
        }
      }
    });
  }

  private _registerCharPlugin(afterDeadline: number, canvasWidth: number = 600): void {
    Chart.pluginService.register({
      beforeDraw: function (c, easing) {
        const chart = c as any;

        if (chart.config.options.chartArea && chart.config.options.chartArea.missingBackgroundColor) {
          const ctx = chart.chart.ctx;
          const chartArea = chart.chartArea;

          const meta = chart.getDatasetMeta(0);

          const dataStartIndex = meta.data[0]._model.x;
          const startIndex = meta.data.length - (afterDeadline + 1);

          if (!meta.data[ startIndex ]) {
            return;
          }

          const start = meta.data[ startIndex ]._model.x;
          const stop  = canvasWidth;

          ctx.save();

          ctx.fillStyle = chart.config.options.chartArea.filledBackgroundColor;

          if (afterDeadline > 0) {
            ctx.fillRect(dataStartIndex, chartArea.top, start - dataStartIndex, chartArea.bottom - chartArea.top);
            ctx.fillStyle = chart.config.options.chartArea.missingBackgroundColor;
            ctx.fillRect(start, chartArea.top, stop - start, chartArea.bottom - chartArea.top);
          } else {
            ctx.fillRect(dataStartIndex, chartArea.top, stop, chartArea.bottom - chartArea.top);
          }

          ctx.restore();
        }
      }
    });
  }

}
